import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainHeader } from '@/components/marketplace/MainHeader';
import { PropertyGrid } from '@/components/marketplace/PropertyGrid';
import { PropertySidebarFilters } from '@/components/marketplace/PropertySidebarFilters';
import { MarketplaceFooter } from '@/components/marketplace/MarketplaceFooter';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Filter, SlidersHorizontal, Home as HomeIcon, Building, Map } from 'lucide-react';
import { Property, PropertyFiltersType } from '@/types/marketplace';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/contexts/TenantContext';
import { useBranding } from '@/contexts/BrandingContext';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

interface MarketplacePropertiesLayoutProps {
  showHeader?: boolean;
  className?: string;
  heroVariant?: string;
}

export const MarketplacePropertiesLayout = ({
  showHeader = true,
  className,
  heroVariant
}: MarketplacePropertiesLayoutProps) => {
  const { tenantId } = useTenant();
  const { branding } = useBranding();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<PropertyFiltersType>({
    location: searchParams.get('location') || "",
    city: searchParams.get('city') || "",
    type: searchParams.get('type') || "all",
    status: searchParams.get('status') || "all",
    priceMin: searchParams.get('priceMin') || "",
    priceMax: searchParams.get('priceMax') || "",
    bedrooms: searchParams.get('bedrooms') || null,
    bathrooms: searchParams.get('bathrooms') || null,
    parking: searchParams.get('parking') || null,
    areaMin: searchParams.get('areaMin') || "",
    areaMax: searchParams.get('areaMax') || "",
    amenities: searchParams.getAll('amenities') || [],
    sortBy: searchParams.get('sortBy') || "newest"
  });
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const propertiesPerPage = 8;

  const loadProperties = async () => {
    if (!tenantId) {
      console.warn("[MarketplacePropertiesLayout] No tenant ID available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("[MarketplacePropertiesLayout] Loading properties for tenant:", tenantId);

      const { data, error } = await supabase
        .from('imoveis')
        .select('*')
        .eq('status', 'ativo')
        .eq('tenant_id', tenantId)
        .eq('visibilidade', 'publico'); // Apenas imóveis públicos

      if (error) {
        console.error("[MarketplacePropertiesLayout] Error loading properties:", error);
        return;
      }

      if (data) {
        console.log("[MarketplacePropertiesLayout] Loaded properties:", data.length);
        const converted: Property[] = data.map((imovel: any) => ({
          id: imovel.id,
          title: imovel.titulo,
          price: Number(imovel.preco),
          location: `${imovel.bairro}, ${imovel.cidade}`,
          bedrooms: imovel.quartos || 0,
          bathrooms: imovel.banheiros || 0,
          area: imovel.area_construida || 0,
          type: imovel.tipo === 'casa' ? 'house' : imovel.tipo === 'terreno' ? 'land' : 'apartment',
          status: imovel.finalidade === 'aluguel' ? 'rent' : 'sale',
          image: imovel.fotos?.[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=400&fit=crop",
          featured: imovel.destaque || false,
          parking: imovel.vagas_garagem || 0,
          _imovelData: imovel
        }));
        setProperties(converted);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      loadProperties();
    }
  }, [tenantId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Sync state when URL params change externally (e.g. back button or redirection while mounted)
  useEffect(() => {
    setFilters(prev => {
      const newLocation = searchParams.get('location') || "";
      const newCity = searchParams.get('city') || "";
      const newType = searchParams.get('type') || "all";
      const newStatus = searchParams.get('status') || "all";
      const newPriceMin = searchParams.get('priceMin') || "";
      const newPriceMax = searchParams.get('priceMax') || "";
      const newBedrooms = searchParams.get('bedrooms') || null;
      const newBathrooms = searchParams.get('bathrooms') || null;
      const newParking = searchParams.get('parking') || null;
      const newAreaMin = searchParams.get('areaMin') || "";
      const newAreaMax = searchParams.get('areaMax') || "";
      const newAmenities = searchParams.getAll('amenities') || [];
      const newSortBy = searchParams.get('sortBy') || "newest";

      // Prevent infinite loop by returning prev if nothing changed
      if (
        prev.location === newLocation &&
        prev.city === newCity &&
        prev.type === newType &&
        prev.status === newStatus &&
        prev.priceMin === newPriceMin &&
        prev.priceMax === newPriceMax &&
        prev.bedrooms === newBedrooms &&
        prev.bathrooms === newBathrooms &&
        prev.parking === newParking &&
        prev.areaMin === newAreaMin &&
        prev.areaMax === newAreaMax &&
        prev.sortBy === newSortBy &&
        JSON.stringify(prev.amenities) === JSON.stringify(newAmenities)
      ) {
        return prev;
      }

      return {
        ...prev,
        location: newLocation,
        city: newCity,
        type: newType as any,
        status: newStatus as any,
        priceMin: newPriceMin,
        priceMax: newPriceMax,
        bedrooms: newBedrooms,
        bathrooms: newBathrooms,
        parking: newParking,
        areaMin: newAreaMin,
        areaMax: newAreaMax,
        amenities: newAmenities,
        sortBy: newSortBy as any
      };
    });
  }, [searchParams]);

  // Filter and sort properties
  const filteredProperties = properties.filter(property => {
    if (filters.city && !property.location.toLowerCase().includes(filters.city.toLowerCase())) {
      return false;
    }
    if (filters.location && !property.location.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    if (filters.type !== "all" && property.type !== filters.type) {
      return false;
    }
    if (filters.status !== "all" && property.status !== filters.status) {
      return false;
    }
    if (filters.priceMin && property.price < parseInt(filters.priceMin)) {
      return false;
    }
    if (filters.priceMax && property.price > parseInt(filters.priceMax)) {
      return false;
    }
    if (filters.bedrooms && filters.bedrooms !== "4+") {
      if (property.bedrooms !== parseInt(filters.bedrooms)) {
        return false;
      }
    }
    if (filters.bedrooms === "4+" && property.bedrooms < 4) {
      return false;
    }
    if (filters.bathrooms && filters.bathrooms !== "4+") {
      if (property.bathrooms !== parseInt(filters.bathrooms)) {
        return false;
      }
    }
    if (filters.bathrooms === "4+" && property.bathrooms < 4) {
      return false;
    }
    if (filters.parking && filters.parking !== "4+") {
      if (property.parking !== parseInt(filters.parking)) {
        return false;
      }
    }
    if (filters.parking === "4+" && property.parking < 4) {
      return false;
    }
    if (filters.areaMin && property.area < parseInt(filters.areaMin)) {
      return false;
    }
    if (filters.areaMax && property.area > parseInt(filters.areaMax)) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    switch (filters.sortBy) {
      case "price-asc":
        return a.price - b.price;
      case "price-desc":
        return b.price - a.price;
      case "newest":
        return b.id.localeCompare(a.id);
      case "oldest":
        return a.id.localeCompare(b.id);
      default:
        return 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const startIndex = (currentPage - 1) * propertiesPerPage;
  const endIndex = startIndex + propertiesPerPage;
  const currentProperties = filteredProperties.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const element = document.getElementById("imoveis-grid");
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFiltersChange = (newFilters: PropertyFiltersType) => {
    setFilters(newFilters);

    const params = new URLSearchParams();
    if (newFilters.city) params.append('city', newFilters.city);
    if (newFilters.location) params.append('location', newFilters.location);
    if (newFilters.type !== 'all') params.append('type', newFilters.type);
    if (newFilters.status !== 'all') params.append('status', newFilters.status);
    if (newFilters.priceMin) params.append('priceMin', newFilters.priceMin);
    if (newFilters.priceMax) params.append('priceMax', newFilters.priceMax);
    if (newFilters.bedrooms) params.append('bedrooms', newFilters.bedrooms);
    if (newFilters.bathrooms) params.append('bathrooms', newFilters.bathrooms);
    if (newFilters.parking) params.append('parking', newFilters.parking);
    if (newFilters.sortBy !== 'newest') params.append('sortBy', newFilters.sortBy);
    newFilters.amenities.forEach(a => params.append('amenities', a));

    setSearchParams(params, { replace: true });
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isAllActive = filters.type === 'all' && filters.status === 'all';
  
  const hasAnyFilter = !isAllActive || 
    filters.location || 
    filters.city || 
    filters.priceMin || 
    filters.priceMax || 
    filters.bedrooms || 
    filters.bathrooms || 
    filters.parking || 
    filters.areaMin || 
    filters.areaMax || 
    filters.amenities.length > 0;

  const featuredProperties = properties.filter(p => p.featured).slice(0, 4);

  const handleClearQuickFilters = () => {
    handleFiltersChange({ ...filters, type: 'all', status: 'all' });
  };

  const handleTypeToggle = (type: string) => {
    handleFiltersChange({
      ...filters,
      type: filters.type === type ? 'all' : type
    });
  };

  const handleStatusToggle = (status: string) => {
    handleFiltersChange({
      ...filters,
      status: filters.status === status ? 'all' : status
    });
  };

  const content = (
    <>
      {/* Filter Track - Visible only if hero is not modern_search */}
      {heroVariant !== 'modern_search' && (
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-4 mb-6 scrollbar-hide w-full px-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <button
          onClick={handleClearQuickFilters}
          className={cn(
            "shrink-0 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all duration-300",
            isAllActive
              ? "bg-zinc-900 text-white shadow-md scale-105"
              : "bg-zinc-100/80 text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900"
          )}
        >
          Todos
        </button>

        <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <SheetTrigger asChild>
            <button
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50 transition-all shadow-sm"
            >
              <SlidersHorizontal className="h-4 w-4 text-zinc-500 mr-1" />
              Filtros
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[90vw] sm:w-[460px] max-w-[460px] p-0 overflow-y-auto">
            <PropertySidebarFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              resultCount={filteredProperties.length}
              className="border-none shadow-none rounded-none h-full"
            />
          </SheetContent>
        </Sheet>

        <div className="shrink-0 w-[1px] h-6 bg-zinc-200 mx-1" aria-hidden="true" />

        <button
          onClick={() => handleTypeToggle('house')}
          className={cn(
            "shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all duration-300",
            filters.type === 'house'
              ? "bg-zinc-900 text-white shadow-md scale-105"
              : "bg-zinc-100/80 text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900"
          )}
        >
          <HomeIcon className={cn("h-4 w-4", filters.type === 'house' ? "text-white" : "text-zinc-400")} />
          Casa
        </button>

        <button
          onClick={() => handleTypeToggle('apartment')}
          className={cn(
            "shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all duration-300",
            filters.type === 'apartment'
              ? "bg-zinc-900 text-white shadow-md scale-105"
              : "bg-zinc-100/80 text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900"
          )}
        >
          <Building className={cn("h-4 w-4", filters.type === 'apartment' ? "text-white" : "text-zinc-400")} />
          Apartamento
        </button>

        <button
          onClick={() => handleTypeToggle('land')}
          className={cn(
            "shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all duration-300",
            filters.type === 'land'
              ? "bg-zinc-900 text-white shadow-md scale-105"
              : "bg-zinc-100/80 text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900"
          )}
        >
          <Map className={cn("h-4 w-4", filters.type === 'land' ? "text-white" : "text-zinc-400")} />
          Terreno
        </button>

        <div className="shrink-0 w-[1px] h-6 bg-zinc-200 mx-1" aria-hidden="true" />

        <button
          onClick={() => handleStatusToggle('sale')}
          className={cn(
            "shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all duration-300",
            filters.status === 'sale'
              ? "bg-zinc-900 text-white shadow-md scale-105"
              : "bg-zinc-100/80 text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900"
          )}
        >
          Comprar
        </button>

        <button
          onClick={() => handleStatusToggle('rent')}
          className={cn(
            "shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all duration-300",
            filters.status === 'rent'
              ? "bg-zinc-900 text-white shadow-md scale-105"
              : "bg-zinc-100/80 text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900"
          )}
        >
          Alugar
        </button>
      </div>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {!showHeader && featuredProperties.length > 0 && !hasAnyFilter && currentPage === 1 && (
        <ScrollReveal animation="up" className="mb-10">
          <h2 className="text-2xl font-bold text-foreground mb-6">Imóveis em Destaque</h2>
          <PropertyGrid properties={featuredProperties} className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" />
        </ScrollReveal>
      )}

      {(!showHeader && !hasAnyFilter && currentPage === 1) && (
        <ScrollReveal animation="up">
          <h2 className="text-2xl font-bold text-foreground mb-6 mt-4">Todos os Imóveis</h2>
        </ScrollReveal>
      )}

      <div className="flex gap-6">
        {/* Properties Grid - Full Width */}
        <div className="flex-1 w-full min-w-0">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="h-64 w-full bg-muted rounded-xl animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-muted rounded animate-pulse"></div>
                  <div className="h-4 w-1/2 bg-muted rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <PropertyGrid 
                properties={currentProperties} 
                className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
                hasActiveFilters={hasAnyFilter}
              />

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="mt-8 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationLink
                          className={cn(
                            "cursor-pointer rounded-xl w-11 h-11 border bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 shadow-sm transition-all flex items-center justify-center",
                            currentPage === 1 && "pointer-events-none opacity-50"
                          )}
                          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                        </PaginationLink>
                      </PaginationItem>

                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              className={cn(
                                "cursor-pointer rounded-xl w-11 h-11 border transition-all text-base font-semibold shadow-sm flex items-center justify-center",
                                currentPage === pageNum
                                  ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:opacity-90 hover:text-white"
                                  : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50 hover:text-zinc-900"
                              )}
                              isActive={currentPage === pageNum}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      <PaginationItem>
                        <PaginationLink
                          className={cn(
                            "cursor-pointer rounded-xl w-11 h-11 border bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 shadow-sm transition-all flex items-center justify-center",
                            currentPage === totalPages && "pointer-events-none opacity-50"
                          )}
                          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                        </PaginationLink>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  // If showHeader is true, wrap in full page layout
  if (showHeader) {
    return (
      <div className={cn("min-h-screen bg-background", className)}>
        <MainHeader />

        <main className="pt-20">
          <div id="imoveis-grid" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-8">
              {content}
            </div>
          </div>
        </main>

        <MarketplaceFooter />
      </div>
    );
  }

  // If showHeader is false, just return the content section
  return (
    <div id="imoveis-grid" className={cn("max-w-7xl mx-auto px-4 sm:px-6 lg:px-8", className)}>
      <div className="pt-4 pb-8">
        {content}
      </div>
    </div>
  );
};
